import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useBitcoinSigner } from '@/hooks/useBitcoinSigner';
import { useNostrLogin } from '@nostrify/react/login';
import { useToast } from '@/hooks/useToast';
import {
  nostrPubkeyToBitcoinAddress,
  validateBitcoinAddress,
  getAddressType,
  addressTypeLabel,
  outputVbytesForType,
  fetchUTXOs,
  getFeeRates,
  buildUnsignedPsbt,
  finalizePsbt,
  broadcastTransaction,
  BroadcastError,
  maxSendable,
  estimateFee,
  formatSats,
  satsToUSD,
  fetchBtcPrice,
} from '@/lib/bitcoin';
import {
  Bitcoin,
  ArrowRight,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Info,
  ChevronDown,
  Flame,
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label as SwitchLabel } from '@/components/ui/label';

type FeeSpeed = 'fast' | 'halfHour' | 'hour' | 'economy';

const BURN_ADDRESSES: { label: string; address: string; description: string }[] = [
  {
    label: '1BurnchainBurnchain…',
    address: '1BurnchainBurnchainBurnchain6cU7ZK',
    description: 'Custom burn address',
  },
  {
    label: '1111111111111114oLvT2',
    address: '1111111111111111111114oLvT2',
    description: 'Null address / Blockstore burn (~65 BTC)',
  },
  {
    label: '1BitcoinEaterAddress…',
    address: '1BitcoinEaterAddressDontSendf59kuE',
    description: 'Classic Bitcoin Eater address (~13 BTC)',
  },
  {
    label: '1A1zP1eP5Q… (Genesis)',
    address: '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa',
    description: "Satoshi's Genesis Block coinbase (~67 BTC)",
  },
];

/** Extensions known to implement window.nostr.signPsbt */
const PSBT_EXTENSIONS = [
  {
    name: 'Alby',
    url: 'https://getalby.com',
    note: 'Supports signPsbt via the Alby browser extension',
  },
  {
    name: 'Nostore',
    url: 'https://nostore.app',
    note: 'iOS/Safari Nostr extension with PSBT support',
  },
];

function ExtensionSigningInfo() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800 text-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors text-left"
      >
        <Info className="w-4 h-4 shrink-0" />
        <span className="flex-1 font-medium">Which extensions support Bitcoin signing?</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2 border-t border-blue-200 dark:border-blue-800 pt-2">
          <p className="text-xs text-blue-600 dark:text-blue-400">
            Only extensions that expose <code className="font-mono bg-blue-100 dark:bg-blue-900/40 px-1 rounded">window.nostr.signPsbt</code> can
            sign Bitcoin transactions. Most standard Nostr extensions do not include this.
          </p>
          <ul className="space-y-1.5">
            {PSBT_EXTENSIONS.map((ext) => (
              <li key={ext.name} className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <div>
                  <a
                    href={ext.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-blue-700 dark:text-blue-300 hover:underline"
                  >
                    {ext.name}
                  </a>
                  <span className="text-blue-600 dark:text-blue-400 ml-1">— {ext.note}</span>
                </div>
              </li>
            ))}
          </ul>
          <p className="text-xs text-blue-600 dark:text-blue-400 pt-1 border-t border-blue-200 dark:border-blue-800">
            If your extension isn't listed, you can still try — it may prompt you to sign.
            If it fails, log in with your <strong>nsec</strong> key for guaranteed support.
          </p>
        </div>
      )}
    </div>
  );
}

interface SendDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-fill the recipient with a pubkey hex or npub. */
  defaultRecipient?: string;
}

type Step = 'form' | 'confirm' | 'signing' | 'broadcasting' | 'success' | 'error';

export function SendDialog({ open, onOpenChange, defaultRecipient }: SendDialogProps) {
  const { user } = useCurrentUser();
  const { canSignPsbt, signPsbt, capability } = useBitcoinSigner();
  const { toast } = useToast();
  const { logins } = useNostrLogin();
  const loginType = logins[0]?.type;

  const [recipient, setRecipient] = useState(defaultRecipient ?? '');
  const [amountSats, setAmountSats] = useState('');
  const [feeSpeed, setFeeSpeed] = useState<FeeSpeed>('halfHour');
  const [step, setStep] = useState<Step>('form');
  const [txid, setTxid] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [debugInfo, setDebugInfo] = useState<string[]>([]);
  const [showDebug, setShowDebug] = useState(false);
  const [estimatedFee, setEstimatedFee] = useState<number | null>(null);
  const [isSendMax, setIsSendMax] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [postToNostr, setPostToNostr] = useState(true);
  const [isPosting, setIsPosting] = useState(false);
  const [posted, setPosted] = useState(false);

  const { mutateAsync: publishNote } = useNostrPublish();

  // Reset when dialog opens/closes
  useEffect(() => {
    if (open) {
      setRecipient(defaultRecipient ?? '');
      setAmountSats('');
      setStep('form');
      setTxid('');
      setErrorMsg('');
      setDebugInfo([]);
      setShowDebug(false);
      setEstimatedFee(null);
      setIsSendMax(false);
      setShowCustom(false);
      setPostToNostr(true);
      setIsPosting(false);
      setPosted(false);
    }
  }, [open, defaultRecipient]);

  const { data: btcPrice } = useQuery({
    queryKey: ['btc-price'],
    queryFn: fetchBtcPrice,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const { data: feeRates } = useQuery({
    queryKey: ['fee-rates'],
    queryFn: getFeeRates,
    enabled: open,
    refetchInterval: 30_000,
  });

  const senderAddress = user ? nostrPubkeyToBitcoinAddress(user.pubkey) : '';

  // fetchUTXOs returns only confirmed UTXOs — safe to spend
  const { data: utxos } = useQuery({
    queryKey: ['bitcoin-utxos', senderAddress],
    queryFn: () => fetchUTXOs(senderAddress),
    enabled: !!senderAddress && open,
    refetchInterval: 30_000,
  });

  // Fetch all UTXOs (including unconfirmed) purely to show a pending balance hint
  const { data: allUtxos } = useQuery({
    queryKey: ['bitcoin-utxos-all', senderAddress],
    queryFn: async () => {
      const res = await fetch(`https://mempool.space/api/address/${senderAddress}/utxo`);
      if (!res.ok) throw new Error('Failed to fetch UTXOs');
      return res.json() as Promise<Array<{ value: number; status: { confirmed: boolean } }>>;
    },
    enabled: !!senderAddress && open,
    refetchInterval: 30_000,
  });

  const totalBalance = utxos?.reduce((sum, u) => sum + u.value, 0) ?? 0;
  const pendingBalance = (allUtxos?.reduce((sum, u) => sum + u.value, 0) ?? 0) - totalBalance;

  function getFeeRate(): number {
    if (!feeRates) return 5;
    switch (feeSpeed) {
      case 'fast': return feeRates.fastestFee;
      case 'halfHour': return feeRates.halfHourFee;
      case 'hour': return feeRates.hourFee;
      case 'economy': return feeRates.economyFee;
    }
  }

  function resolveRecipientAddress(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (validateBitcoinAddress(trimmed)) return trimmed;
    return '';
  }

  const resolvedAddress = resolveRecipientAddress(recipient);
  const isValidRecipient = !!resolvedAddress;
  const isNpubInput = false;

  // Detect address type for the resolved recipient
  const recipientAddrType = resolvedAddress ? getAddressType(resolvedAddress) : 'unknown';
  const recipientVbytes = outputVbytesForType(recipientAddrType);

  const parsedAmount = parseInt(amountSats, 10);
  const isValidAmount = !isNaN(parsedAmount) && parsedAmount > 546;

  // Estimate fee on the fly using accurate per-type output vbytes
  useEffect(() => {
    if (!utxos || !feeRates || !isValidAmount) { setEstimatedFee(null); return; }
    const rate = getFeeRate();
    const numInputs = utxos.length;
    const change = totalBalance - parsedAmount - estimateFee(numInputs, 2, rate, recipientVbytes);
    const numOutputs = change >= 546 ? 2 : 1;
    setEstimatedFee(estimateFee(numInputs, numOutputs, rate, recipientVbytes));
  }, [utxos, feeRates, amountSats, feeSpeed, parsedAmount, isValidAmount, recipientVbytes]);

  function handleSendMax() {
    if (!utxos) return;
    const rate = getFeeRate();
    const max = maxSendable(totalBalance, utxos.length, rate, recipientVbytes);
    setAmountSats(String(max));
    setIsSendMax(true);
  }

  async function handleSend() {
    if (!user || !signPsbt || !utxos || !isValidRecipient || !isValidAmount) return;

    const log: string[] = [];
    const addLog = (msg: string) => { log.push(msg); console.info('[Send]', msg); };

    try {
      setStep('signing');
      setDebugInfo([]);

      const rate = getFeeRate();
      addLog(`Building tx: ${parsedAmount} sats → ${resolvedAddress}`);
      addLog(`UTXOs: ${utxos.length}, total: ${utxos.reduce((s, u) => s + u.value, 0)} sats, fee rate: ${rate} sat/vB`);

      const { psbtHex, localToken, fee } = buildUnsignedPsbt(user.pubkey, resolvedAddress, parsedAmount, utxos, rate);
      addLog(`Tx built. Fee: ${fee} sats`);
      setEstimatedFee(fee);

      // nsec logins sign locally using the structured localToken (avoids the
      // broken bitcoinjs Transaction.toBuffer/fromBuffer in the browser bundle).
      // Extension and bunker logins receive the real BIP-174 PSBT hex.
      const signingPayload = loginType === 'nsec' ? localToken : psbtHex;
      addLog(`Signing (${capability})…`);
      const signedHex = await signPsbt(signingPayload);
      addLog('Signed OK');
      setStep('broadcasting');

      addLog('Finalizing…');
      const txHex = finalizePsbt(signedHex);
      addLog(`Raw tx hex length: ${txHex.length} chars`);

      addLog('Broadcasting…');
      const newTxid = await broadcastTransaction(txHex);
      addLog(`Broadcast OK: ${newTxid}`);
      setTxid(newTxid);
      setStep('success');
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err);
      const rawLower = raw.toLowerCase();

      let msg: string;

      if (err instanceof BroadcastError) {
        // Mempool rejected the transaction — show the exact reason
        msg = raw;
      } else if (
        rawLower.includes("doesn't support") ||
        rawLower.includes('does not support') ||
        rawLower.includes('signpsbt') ||
        rawLower.includes('sign_psbt')
      ) {
        msg = "Your signer doesn't support Bitcoin signing. Log in with your nsec key to send.";
      } else if (rawLower.includes('insufficient funds')) {
        msg = raw;
      } else if (rawLower.includes('user rejected') || rawLower.includes('user cancelled') || rawLower.includes('user denied')) {
        msg = 'Signing was cancelled.';
      } else {
        msg = raw || 'An unexpected error occurred. Please try again.';
      }

      addLog(`ERROR: ${err instanceof Error ? err.stack ?? err.message : String(err)}`);
      console.error('[SendDialog] transaction failed:', err);
      setDebugInfo([...log]);
      setErrorMsg(msg);
      setStep('error');
      toast({ title: 'Transaction failed', description: msg, variant: 'destructive' });
    }
  }

  const feeOptions: { id: FeeSpeed; label: string; time: string }[] = [
    { id: 'fast', label: 'Fast', time: '~10 min' },
    { id: 'halfHour', label: 'Normal', time: '~30 min' },
    { id: 'hour', label: 'Slow', time: '~1 hr' },
    { id: 'economy', label: 'Economy', time: '~1 day' },
  ];

  const canProceed = isValidRecipient && isValidAmount && canSignPsbt && !!utxos;
  const hasEnoughFunds = parsedAmount + (estimatedFee ?? 0) <= totalBalance;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-orange-500">
            <Bitcoin className="w-5 h-5" />
            Send Bitcoin
          </DialogTitle>
          <DialogDescription aria-describedby={undefined}>
            Burn sats sent to your npub.
          </DialogDescription>
        </DialogHeader>

        {/* FORM STEP */}
        {(step === 'form' || step === 'confirm') && (
          <div className="space-y-4">
            {/* Balance chip */}
            <div className="rounded-lg bg-muted px-3 py-2 text-sm space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Confirmed balance</span>
                <span className="font-mono font-medium">
                  {formatSats(totalBalance)} sats
                  {btcPrice && (
                    <span className="text-muted-foreground ml-1">
                      ({satsToUSD(totalBalance, btcPrice)})
                    </span>
                  )}
                </span>
              </div>
              {pendingBalance > 0 && (
                <div className="flex items-center justify-between text-xs text-amber-600 dark:text-amber-400">
                  <span>Pending (unconfirmed — not spendable yet)</span>
                  <span className="font-mono">+{formatSats(pendingBalance)} sats</span>
                </div>
              )}
            </div>

            {/* Burn address picker */}
            <div className="space-y-1.5">
              <Label>Burn address</Label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                value={showCustom ? 'custom' : (BURN_ADDRESSES.some((b) => b.address === recipient) ? recipient : '')}
                onChange={(e) => {
                  if (e.target.value === 'custom') {
                    setShowCustom(true);
                    setRecipient('');
                  } else if (e.target.value) {
                    setShowCustom(false);
                    setRecipient(e.target.value);
                  }
                }}
              >
                <option value="">— select a burn address —</option>
                {BURN_ADDRESSES.map((b) => (
                  <option key={b.address} value={b.address}>
                    {b.address} · {b.description}
                  </option>
                ))}
                <option value="custom">Custom Bitcoin address…</option>
              </select>
              {!showCustom && recipient && BURN_ADDRESSES.some((b) => b.address === recipient) && (
                <p className="text-xs text-muted-foreground">
                  {BURN_ADDRESSES.find((b) => b.address === recipient)?.description}
                </p>
              )}
            </div>

            {/* Custom recipient — only shown when "Custom" is selected */}
            {showCustom && (
            <div className="space-y-1.5">
              <Label htmlFor="recipient">Custom Bitcoin address</Label>
              <Input
                id="recipient"
                placeholder="bc1p…, bc1q…, 1… or 3…"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                className={recipient && !isValidRecipient ? 'border-destructive' : ''}
                autoFocus
              />
              {recipient && !isValidRecipient && (
                <p className="text-xs text-destructive">Not a valid Bitcoin address.</p>
              )}
              {isValidRecipient && (
                <div className="flex items-center gap-2 flex-wrap">
                  {isNpubInput && (
                    <p className="text-xs text-muted-foreground font-mono truncate">
                      → {resolvedAddress}
                    </p>
                  )}
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${
                    recipientAddrType === 'p2tr'
                      ? 'border-orange-300 bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400'
                      : recipientAddrType === 'p2wpkh' || recipientAddrType === 'p2wsh'
                      ? 'border-blue-300 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                      : 'border-muted-foreground/30 bg-muted text-muted-foreground'
                  }`}>
                    {addressTypeLabel(recipientAddrType)}
                  </span>
                </div>
              )}
            </div>
            )}

            {/* Amount */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="amount">Amount (sats)</Label>
                <button
                  type="button"
                  onClick={handleSendMax}
                  className="text-xs text-orange-500 hover:underline"
                >
                  Send max
                </button>
              </div>
              <Input
                id="amount"
                type="number"
                min={547}
                placeholder="e.g. 10000"
                value={amountSats}
                onChange={(e) => { setAmountSats(e.target.value); setIsSendMax(false); }}
                className={amountSats && !isValidAmount ? 'border-destructive' : ''}
              />
              {amountSats && !isValidAmount && (
                <p className="text-xs text-destructive">Minimum amount is 547 sats (dust limit).</p>
              )}
              {isValidAmount && btcPrice && (
                <p className="text-xs text-muted-foreground">
                  ≈ {satsToUSD(parsedAmount, btcPrice)}
                </p>
              )}
            </div>

            {/* Fee speed */}
            <div className="space-y-1.5">
              <Label>Fee speed</Label>
              <div className="grid grid-cols-4 gap-1.5">
                {feeOptions.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setFeeSpeed(opt.id)}
                    className={`rounded-lg border py-1.5 text-center text-xs transition-colors ${
                      feeSpeed === opt.id
                        ? 'border-orange-500 bg-orange-500/10 text-orange-600 dark:text-orange-400'
                        : 'border-border text-muted-foreground hover:border-orange-400'
                    }`}
                  >
                    <div className="font-medium">{opt.label}</div>
                    <div className="text-[10px] opacity-70">{opt.time}</div>
                    {feeRates && (
                      <div className="text-[10px] opacity-60">
                        {(() => {
                          const r = opt.id === 'fast' ? feeRates.fastestFee
                            : opt.id === 'halfHour' ? feeRates.halfHourFee
                            : opt.id === 'hour' ? feeRates.hourFee
                            : feeRates.economyFee;
                          return r < 1 ? r.toFixed(2) : Math.round(r);
                        })()} sat/vB
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Fee summary */}
            {estimatedFee !== null && isValidAmount && (
              <div className="rounded-lg bg-muted/50 border px-3 py-2 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-mono">{formatSats(parsedAmount)} sats</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Network fee</span>
                  <span className="font-mono">{formatSats(estimatedFee)} sats</span>
                </div>
                <Separator className="my-1" />
                <div className="flex justify-between font-semibold">
                  <span>Total</span>
                  <span className={`font-mono ${!hasEnoughFunds ? 'text-destructive' : ''}`}>
                    {formatSats(parsedAmount + estimatedFee)} sats
                  </span>
                </div>
                {!hasEnoughFunds && (
                  <p className="text-xs text-destructive pt-1">Insufficient balance.</p>
                )}
              </div>
            )}

            {/* Extension signing info */}
            {loginType === 'extension' && <ExtensionSigningInfo />}

            {/* Unsupported signer warning */}
            {capability === 'unsupported' && (
              <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  Your current login doesn't support Bitcoin signing. Log in with your nsec to send.
                </span>
              </div>
            )}

            <Button
              className="w-full bg-orange-500 hover:bg-orange-600 text-white"
              disabled={!canProceed || !hasEnoughFunds || !isValidAmount}
              onClick={handleSend}
            >
              <ArrowRight className="w-4 h-4 mr-2" />
              Send {isValidAmount ? `${formatSats(parsedAmount)} sats` : ''}
            </Button>
          </div>
        )}

        {/* SIGNING STEP */}
        {step === 'signing' && (
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <Loader2 className="w-10 h-10 animate-spin text-orange-500" />
            <div className="text-center">
              <p className="font-semibold">Signing transaction…</p>
              <p className="text-sm text-muted-foreground mt-1">
                {loginType === 'extension' ? 'Check your browser extension for a signing prompt.' : 'Please wait.'}
              </p>
            </div>
          </div>
        )}

        {/* BROADCASTING STEP */}
        {step === 'broadcasting' && (
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <Loader2 className="w-10 h-10 animate-spin text-orange-500" />
            <div className="text-center">
              <p className="font-semibold">Broadcasting…</p>
              <p className="text-sm text-muted-foreground mt-1">Sending to the Bitcoin network.</p>
            </div>
          </div>
        )}

        {/* SUCCESS STEP */}
        {step === 'success' && (
          <div className="flex flex-col items-center py-4 gap-4 text-center w-full">
            <div className="relative">
              <CheckCircle2 className="w-12 h-12 text-green-500" />
              <Flame className="w-5 h-5 text-orange-500 absolute -top-1 -right-1" />
            </div>
            <div>
              <p className="font-semibold text-lg">Sats burned!</p>
              <p className="text-sm text-muted-foreground mt-1">
                {formatSats(parsedAmount)} sats gone forever. Bitcoin is now more scarce.
              </p>
            </div>
            {txid && (
              <a
                href={`https://mempool.space/tx/${txid}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-sm text-orange-500 hover:underline font-mono break-all"
              >
                {txid.slice(0, 16)}…{txid.slice(-8)}
                <ExternalLink className="w-3 h-3 shrink-0" />
              </a>
            )}

            {/* Nostr post prompt */}
            {!posted ? (
              <div className="w-full rounded-lg border bg-muted/40 p-4 space-y-3 text-left">
                <div className="flex items-center justify-between gap-3">
                  <SwitchLabel htmlFor="post-nostr" className="text-sm font-medium cursor-pointer flex-1">
                    Post a note to Nostr
                  </SwitchLabel>
                  <Switch
                    id="post-nostr"
                    checked={postToNostr}
                    onCheckedChange={setPostToNostr}
                  />
                </div>
                {postToNostr && (
                  <p className="text-xs text-muted-foreground italic border-l-2 border-orange-400 pl-3 whitespace-pre-line">
                    {`I just burned ${formatSats(parsedAmount)} sats that was sent to my npub in protest. Not your keys, not your coins — and now, not anyone's coins. 🔥\n\ntx: mempool.space/tx/${txid ? txid.slice(0, 16) : '…'}\n\n${window.location.origin}`}
                  </p>
                )}
                <Button
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                  size="sm"
                  disabled={isPosting}
                  onClick={async () => {
                    if (!postToNostr) { onOpenChange(false); return; }
                    setIsPosting(true);
                    try {
                      const burnAddr = BURN_ADDRESSES.some((b) => b.address === recipient)
                        ? recipient
                        : recipient;
                      await publishNote({
                        kind: 1,
                        content: `I just burned ${formatSats(parsedAmount)} sats that was sent to my npub in protest. Not your keys, not your coins — and now, not anyone's coins. 🔥\n\ntx: https://mempool.space/tx/${txid}\n\n${window.location.origin}`,
                        tags: [
                          ['r', `https://mempool.space/tx/${txid}`],
                          ['r', `https://mempool.space/address/${burnAddr}`],
                          ['r', window.location.origin],
                        ],
                      });
                      setPosted(true);
                    } catch {
                      toast({ title: 'Failed to post note', variant: 'destructive' });
                    } finally {
                      setIsPosting(false);
                    }
                  }}
                >
                  {isPosting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Flame className="w-4 h-4 mr-2" />}
                  {postToNostr ? 'Post & close' : 'Close'}
                </Button>
              </div>
            ) : (
              <div className="w-full space-y-3">
                <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                  Note posted to Nostr 🔥
                </p>
                <div className="flex gap-2 w-full">
                  <Button variant="outline" className="flex-1" onClick={() => { setStep('form'); setAmountSats(''); setTxid(''); setPosted(false); }}>
                    Burn more
                  </Button>
                  <Button className="flex-1 bg-orange-500 hover:bg-orange-600 text-white" onClick={() => onOpenChange(false)}>
                    Done
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ERROR STEP */}
        {step === 'error' && (
          <div className="flex flex-col items-center justify-center py-6 gap-4 text-center">
            <AlertCircle className="w-12 h-12 text-destructive" />
            <div className="w-full space-y-2">
              <p className="font-semibold text-lg">Transaction failed</p>
              <div className="rounded-lg bg-destructive/10 border border-destructive/20 px-3 py-2 text-left">
                <p className="text-sm text-destructive break-words">{errorMsg}</p>
              </div>
            </div>

            {/* Debug log — collapsed by default */}
            {debugInfo.length > 0 && (
              <div className="w-full">
                <button
                  type="button"
                  onClick={() => setShowDebug((v) => !v)}
                  className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mx-auto"
                >
                  <ChevronDown className={`w-3 h-3 transition-transform ${showDebug ? 'rotate-180' : ''}`} />
                  {showDebug ? 'Hide' : 'Show'} debug info
                </button>
                {showDebug && (
                  <pre className="mt-2 w-full text-left text-[10px] font-mono bg-muted rounded-lg p-2 overflow-auto max-h-40 whitespace-pre-wrap break-all">
                    {debugInfo.join('\n')}
                  </pre>
                )}
              </div>
            )}

            <Button className="w-full" variant="outline" onClick={() => setStep('form')}>
              Try again
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
